import { useMemo } from "react";
import { useSelector } from "react-redux";

import ChoiceChips from "components/Form/choiceChips";
import FilterSelect from "components/Form/filterSelect";
import { PERIOD_OPTIONS } from "./useStatsData";

const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" });

const selected = (ids, options) => options.filter((option) => ids.includes(String(option.value)));

/** One row above every chart: the slice of the company (cost center,
 *  department) and, on the turnover page, the period. Everything on the
 *  page follows it. */
const StatsFilterBar = ({ filters, update, reset, activeCount, showPeriod = false }) => {
  const { costCenter, costCenterLoading, department, departmentLoading } = useSelector((state) => state.common);
  const costCenterOptions = useMemo(
    // The code is part of the label and its own field, so typing "CS-099"
    // finds the cost center as well as typing its name.
    () =>
      (costCenter || [])
        .map((c) => ({ label: c.code ? `${c.name || c.code} (${c.code})` : c.name, value: c.id, code: c.code || "" }))
        .sort(byLabel),
    [costCenter]
  );
  const departmentOptions = useMemo(
    () => (department || []).map((d) => ({ label: d.name, value: d.id })).sort(byLabel),
    [department]
  );
  const ids = (next) => (next || []).map((option) => String(option.value));

  return (
    <div className="ws-filters" role="group" aria-label="Statistics filters">
      <div className="ws-filters__field">
        <label htmlFor="ws-filter-cost-center">Cost center</label>
        <FilterSelect
          inputId="ws-filter-cost-center"
          isMulti checkboxMenu selectAll maxVisibleValues={2} isClearable menuPosition="fixed"
          options={costCenterOptions} value={selected(filters.costCenters, costCenterOptions)}
          onChange={(next) => update({ costCenters: ids(next) })}
          optionLabel="label" optionValue="value" placeholder="All cost centers (search name or code)"
          isLoading={costCenterLoading} noOptionsLabel="cost centers"
        />
      </div>
      <div className="ws-filters__field">
        <label htmlFor="ws-filter-department">Department</label>
        <FilterSelect
          inputId="ws-filter-department"
          isMulti checkboxMenu selectAll maxVisibleValues={2} isClearable menuPosition="fixed"
          options={departmentOptions} value={selected(filters.departments, departmentOptions)}
          onChange={(next) => update({ departments: ids(next) })}
          optionLabel="label" optionValue="value" placeholder="All departments"
          isLoading={departmentLoading} noOptionsLabel="departments"
        />
      </div>
      {showPeriod && (
        <div className="ws-filters__field ws-filters__period">
          <span className="ws-filters__label" id="ws-filter-period">Period</span>
          <ChoiceChips
            ariaLabel="Period"
            allowClear={false}
            options={PERIOD_OPTIONS}
            value={filters.months}
            onChange={(option) => update({ months: option.value })}
          />
        </div>
      )}
      {activeCount > 0 && (
        <button type="button" className="table-link ws-filters__reset" onClick={reset}>
          Clear filters
        </button>
      )}
    </div>
  );
};

export default StatsFilterBar;
